import BottomSheet from "../common/bottom-sheet";
import type { UserCoupon } from "../../__generated__/graphql";

interface CouponSelectBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  coupons: UserCoupon[];
  selectedCouponId: number | null;
  onSelect: (coupon: UserCoupon | null) => void;
  isCouponDisabled: (coupon: UserCoupon) => boolean;
}

function formatDiscount(coupon: UserCoupon["coupon"]): string {
  if (coupon.discountType === "PERCENT") {
    return `${coupon.discountValue}% 할인`;
  }
  return `${Number(coupon.discountValue).toLocaleString("ko-KR")}원 할인`;
}

function formatExpiredAt(expiredAt: string | null | undefined): string | null {
  if (!expiredAt) return null;
  return new Date(expiredAt).toLocaleString("ko-KR", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function CouponSelectBottomSheet({
  isOpen,
  onClose,
  coupons,
  selectedCouponId,
  onSelect,
  isCouponDisabled,
}: CouponSelectBottomSheetProps) {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} disableDrag>
      <div className="pb-2">
        <h2 className="text-lg font-bold text-black mb-1">사용할 쿠폰 선택</h2>
        <p className="text-sm text-gray-500 mb-4">
          만료일·최소 주문금액으로 쿠폰을 구분해 주세요
        </p>

        <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-2">
          <button
            type="button"
            onClick={() => {
              onSelect(null);
              onClose();
            }}
            className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
              selectedCouponId == null
                ? "border-[#1562fc] bg-blue-50"
                : "border-gray-200 bg-white hover:bg-gray-50"
            }`}
          >
            <p className="text-sm font-semibold text-black">쿠폰 사용 안 함</p>
          </button>

          {coupons.length === 0 && (
            <p className="py-6 text-center text-sm text-gray-400">
              사용 가능한 쿠폰이 없어요
            </p>
          )}

          {coupons.map((userCoupon) => {
            const disabled = isCouponDisabled(userCoupon);
            const selected = selectedCouponId === userCoupon.id;
            const expiredLabel = formatExpiredAt(userCoupon.expiredAt);

            return (
              <button
                key={userCoupon.id}
                type="button"
                disabled={disabled}
                onClick={() => {
                  onSelect(userCoupon);
                  onClose();
                }}
                className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${
                  disabled
                    ? "cursor-not-allowed border-gray-100 bg-gray-50 opacity-60"
                    : selected
                      ? "border-[#1562fc] bg-blue-50"
                      : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-black leading-snug">
                      {userCoupon.coupon.title}
                    </p>
                    <p className="mt-0.5 text-sm font-semibold text-[#1562fc]">
                      {formatDiscount(userCoupon.coupon)}
                    </p>
                    <ul className="mt-2 space-y-0.5 text-xs text-gray-500">
                      {expiredLabel && <li>만료: {expiredLabel}</li>}
                      {userCoupon.coupon.minOrderPrice ? (
                        <li>
                          최소 주문:{" "}
                          {Number(
                            userCoupon.coupon.minOrderPrice,
                          ).toLocaleString("ko-KR")}
                          원
                        </li>
                      ) : (
                        <li>최소 주문: 제한 없음</li>
                      )}
                      {userCoupon.coupon.discountType === "PERCENT" &&
                        userCoupon.coupon.maxDiscountAmount != null && (
                          <li>
                            최대 할인:{" "}
                            {Number(
                              userCoupon.coupon.maxDiscountAmount,
                            ).toLocaleString("ko-KR")}
                            원
                          </li>
                        )}
                      <li>
                        {userCoupon.coupon.storeId
                          ? "이 매장 전용 쿠폰"
                          : "전체 매장 사용 가능"}
                      </li>
                      {disabled && (
                        <li className="text-red-500">최소 주문금액 미달</li>
                      )}
                    </ul>
                  </div>
                  {selected && !disabled && (
                    <span className="shrink-0 text-[#1562fc] text-sm font-bold">
                      ✓
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </BottomSheet>
  );
}
